import { Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, type ReactNode } from 'react';
import { LockKeyhole } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSessionExpired } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { LoginForm } from './LoginForm';

export function AuthGate({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const expired = useSessionExpired();
  const location = useLocation();
  const qc = useQueryClient();

  // React 18 has no typed `inert` prop, so set the attribute directly.
  const setInert = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) return;
      if (expired) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
    },
    [expired],
  );

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        <div className="animate-pulse text-sm">Checking session…</div>
      </div>
    );
  }
  if (!user) {
    const from = location.pathname + location.search + location.hash;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  // The session ran out while the app was open: keep the page mounted (so
  // the route and any unsaved edits survive) and sign in on top of it.
  return (
    <>
      <div ref={setInert} className="contents" aria-hidden={expired || undefined}>
        {children}
      </div>
      {expired && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="session-expired-title"
          className="fixed inset-0 z-[100] grid place-items-center bg-background/70 p-6 backdrop-blur-sm"
        >
          <Card className="w-full max-w-sm shadow-xl">
            <CardHeader className="space-y-3">
              <div className="grid h-10 w-10 place-items-center rounded-md bg-primary/10 text-primary">
                <LockKeyhole className="h-5 w-5" />
              </div>
              <CardTitle id="session-expired-title" className="text-xl">
                Session expired
              </CardTitle>
              <CardDescription>
                Sign in again to continue. You stay on this page and unsaved changes are kept.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LoginForm
                defaultEmail={user.email}
                onSuccess={() => void qc.invalidateQueries()}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
